import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Breadcrumb } from "../../components/Breadcrumb";
import {
  PaymentProcessorList,
  paymentProcessorListLoader,
} from "./PaymentProcessorList";

import {
  PaymentProcessorEdit,
  paymentProcessorEditAction,
  paymentProcessorEditLoader,
} from "./PaymentProcessorEdit";
import {
  PaymentProcessorCreate,
  paymentProcessorCreateAction,
  paymentProcessorsLoader,
} from "./PaymentProcessorCreate";
import { PaymentProcessor } from "../../types/paymentProcessor";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { DeletePaymentProcessor } from "./mutations";
import { toast } from "react-hot-toast";
import {
  PaymentProcessorClone,
  paymentProcessorCloneAction,
  paymentProcessorCloneLoader,
} from "./PaymentProcessorClone";
import { requireAuth } from "../auth/RequireAuth";
import { userStore } from "../../store/user";
import { ProcessorCapability, processorAccessFor } from "./access";

const PaymentProcessorsIndex = () => {
  const location = useLocation();
  const navigate = useNavigate();

  if (location.pathname === "/payment-processors") {
    navigate(`/payment-processors/list`);
  }

  return (
    <>
      <Outlet />
    </>
  );
};

const client = createApolloClient();

// Payment processors are the settlement path — a swapped processor quietly
// redirects money. Sidebar.tsx hides the nav item on these same rules, but
// hiding a link is not a control: these URLs are guessable and the actions are
// reachable by direct POST, so the routes below re-check on every entry.
const processorAccess = () => processorAccessFor(userStore.getState().user);

// Gates a route loader: bounce to the dashboard unless the capability is held.
const guardLoader = (
  capability: ProcessorCapability,
  loader?: (args: LoaderFunctionArgs) => unknown
) =>
  requireAuth(undefined, async (args: LoaderFunctionArgs) => {
    if (!processorAccess()[capability]) return redirect("/");
    return loader ? await loader(args) : null;
  });

// Gates a route action. Loaders only cover navigation — a form POST hits the
// action directly without one, so every mutation re-checks independently.
const guardAction =
  (
    capability: ProcessorCapability,
    action: (args: ActionFunctionArgs) => unknown
  ) =>
  async (args: ActionFunctionArgs) => {
    if (!userStore.getState().user) return redirect("/login");
    if (!processorAccess()[capability]) return redirect("/");
    return action(args);
  };

const paymentProcessorDeleteAction = async ({ params }: ActionFunctionArgs) => {
  const { id } = params;
  try {
    await Promise.all([
      client.mutate({
        mutation: DeletePaymentProcessor,
        variables: { id },
      }),
    ]);
    toast.success("Menu deleted");
    return redirect("/payment-processors/list");
  } catch {
    toast.error("Payment Processor could not be deleted");
  }
};

export const paymentProcessorsRouter = {
  path: "/payment-processors",
  id: "payment-processors",
  element: <PaymentProcessorsIndex />,
  handle: {
    crumb: () => (
      <Breadcrumb name="PaymentProcessors" href="/payment-processors/list" />
    ),
  },
  children: [
    {
      path: "list",
      id: "payment-processors-list",
      element: <PaymentProcessorList />,
      loader: guardLoader("view", paymentProcessorListLoader),
    },
    {
      path: "create",
      id: "payment-processors-create",
      element: <PaymentProcessorCreate />,
      action: guardAction("edit", paymentProcessorCreateAction),
      loader: guardLoader("edit", paymentProcessorsLoader),
      handle: {
        crumb: () => (
          <Breadcrumb
            name="Create Payment Processor"
            href="/payment-processors/create"
          />
        ),
      },
    },
    {
      path: ":id/clone",
      id: "payment-processors-clone",
      element: <PaymentProcessorClone />,
      action: guardAction("edit", paymentProcessorCloneAction),
      loader: guardLoader("edit", paymentProcessorCloneLoader),
      handle: {
        crumb: (data: PaymentProcessor) => (
          <Breadcrumb
            name={`Clone ${data.payment_processor}`}
            href={`/payment-processors/${data.id}/clone`}
          />
        ),
      },
    },
    {
      path: ":id/edit",
      id: "payment-processors-edit",
      element: <PaymentProcessorEdit />,
      // "view" opens the page read-only (the component disables the fieldset);
      // "edit" is what it takes to actually submit a change.
      loader: guardLoader("view", paymentProcessorEditLoader),
      action: guardAction("edit", paymentProcessorEditAction),
      handle: {
        crumb: (data: PaymentProcessor) => (
          <Breadcrumb
            name={`Edit ${data.payment_processor}`}
            href={`/payment-processors/${data.id}/edit`}
          />
        ),
      },
    },
    {
      path: ":id/delete",
      id: "payment-processors-delete",
      action: guardAction("remove", paymentProcessorDeleteAction),
    },
  ],
};
